import { Circle, Square, Triangle, Star, Hexagon, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ICONS = { circle: Circle, square: Square, triangle: Triangle, star: Star, hexagon: Hexagon };

export default function QuestionCard({ question, value, onChange, onNext, saving, isLast }) {
  if (!question) return null;
  const { type, prompt, options, section, image_url } = question;

  const renderOptions = () => {
    if (type === "likert5") {
      return (
        <div className="grid grid-cols-1 gap-3" data-testid="question-options-likert">
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`assessment-option-card-${opt.id}`}
              onClick={() => onChange(opt.id)}
              className={cn(
                "rounded-xl border p-4 text-sm font-medium transition-all text-left flex items-center justify-between gap-3",
                value === opt.id
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-300"
                  : "border-slate-200 bg-white hover:border-indigo-300 text-slate-600"
              )}
            >
              <span>{opt.label}</span>
              {value === opt.id && <CheckCircle2 className="h-5 w-5 text-indigo-600 shrink-0" />}
            </button>
          ))}
        </div>
      );
    }
    if (type === "visual_pattern") {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="question-options-visual">
          {options.map((opt) => {
            const Icon = ICONS[opt.icon] || Circle;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`assessment-option-card-${opt.id}`}
                onClick={() => onChange(opt.id)}
                className={cn(
                  "rounded-xl border p-6 flex flex-col items-center gap-2 transition-all",
                  value === opt.id
                    ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300"
                    : "border-slate-200 bg-white hover:border-indigo-300"
                )}
              >
                <Icon className="h-8 w-8 text-indigo-600" />
                <span className="text-xs text-slate-500">{opt.label}</span>
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="question-options-default">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            data-testid={`assessment-option-card-${opt.id}`}
            onClick={() => onChange(opt.id)}
            className={cn(
              "rounded-xl border p-5 text-left text-sm font-medium transition-all",
              value === opt.id
                ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-300"
                : "border-slate-200 bg-white hover:border-indigo-300 text-slate-600"
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full" data-testid="question-card">
      {image_url && (
        <div className="mb-6 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50" data-testid="question-illustration-wrap">
          <img
            src={image_url}
            alt=""
            data-testid="question-illustration-image"
            className="w-full h-44 sm:h-56 object-cover"
            loading="eager"
          />
        </div>
      )}
      <p className="text-xs uppercase tracking-[0.2em] font-medium text-indigo-600 mb-4">{section}</p>
      <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-8 leading-snug">{prompt}</h2>
      {renderOptions()}
      <div className="mt-10 flex items-center justify-between">
        <span className="text-xs text-slate-400" data-testid="autosave-indicator">
          {saving ? "Saving…" : value !== undefined && value !== null ? "Response Saved" : ""}
        </span>
        <Button
          data-testid="assessment-next-button"
          disabled={value === undefined || value === null || saving}
          onClick={onNext}
          className="rounded-full px-8 h-11 bg-indigo-600 hover:bg-indigo-700"
        >
          {isLast ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
}
